<template>
  <S1DisclaimerModal
    :open="is_s1_disclaimer_modal_open"
    @close-modal="is_s1_disclaimer_modal_open = false"
  />
  <SignPendingS1InvestmentsModal
    :investments="pendingS1Investments"
    :open="is_sign_pending_s1_modal_open"
    @close-modal="is_sign_pending_s1_modal_open = false"
    @generate-signing-link="handleGeneratePendingS1SigningLink"
  />
  <OptOutS1InvestmentsModal
    :investments="optOutInvestments"
    :open="is_opt_out_s1_modal_open"
    @close-modal="is_opt_out_s1_modal_open = false"
    @generate-signing-link="handleGeneratePendingS1SigningLink"
  />
  <DocumentPreviewModal
    v-if="statement_preview_file"
    :open="is_statement_preview_modal_open"
    :file="statement_preview_file"
    @close-modal="closeStatementPreview"
  />
  <SignInvestmentLoader
    v-if="UIStore.is_loading"
    ref="player"
    :key="UIStore.is_dark_mode"
    :animation-data="animationData"
  />
  <div class="w-full pb-6">
    <Modal
      :open="showMoreActionsModal"
      @close-modal="showMoreActionsModal = false"
      customHeight="h-value-15 md:h-value-23 lg:h-160"
      customWidth="lg:w-240"
    >
      <template v-slot:modal-header>
        <div class="flex items-center justify-between">
          <div class="flex flex-row items-center">
            <div
              class="mr-4 flex h-8 w-8 items-center justify-center rounded-md bg-custom-navy bg-opacity-20 p-1"
            >
              <Icon name="Bell" class="h-6 w-6 text-error" :stroke-width="2" />
            </div>
            <div class="font-bold text-custom-navy-2">Notifications</div>
          </div>
        </div>
      </template>

      <template v-slot:modal-body>
        <div class="p-5">
          <div
            v-for="(actions, priority) in groupedActionItems"
            :key="priority"
            class="flex space-y-4 w-full items-start"
          >
            <div
              class="font-semibold flex items-start mt-4 w-50 text-base"
              :class="priority == 1 ? 'text-error' : priority == 2 ? 'text-info' : 'text-success'"
            >
              <Icon
                name="Asterisk"
                :class="priority == 1 ? 'text-error' : priority == 2 ? 'text-info' : 'text-success'"
              />
              <p>
                {{
                  priority == 1
                    ? "Urgently required action"
                    : priority == 2
                      ? "Required action"
                      : "Great offer"
                }}
              </p>
            </div>
            <div class="space-y-2 w-full">
              <div v-for="action in actions" :key="action.id">
                <DetailBanner
                  :variant="action.variant"
                  :has_error_outline="action.has_error_outline"
                  :button_flex="true"
                  class="w-full"
                >
                  <template #body>
                    <div
                      class="text-lg font-medium font-stretch-normal not-italic leading-none tracking-[-0.56] text-left text-strongest"
                    >
                      {{ action.header_text }}
                    </div>
                    <span
                      v-for="(segment, i) in action.body_text"
                      :key="i"
                      class="font-inter text-sm font-medium font-stretch-normal not-italic leading-none tracking-normal text-left"
                      :class="segment.class"
                    >
                      {{ segment.text }}
                    </span>
                  </template>
                  <template #ctas>
                    <div>
                      <Button
                        :variant="action.button_variant"
                        @click="
                          () => {
                            dashboardCtaClicked(action.button_label);
                            action.action();
                          }
                        "
                      >
                        <template #label>{{ action.button_label }}</template>
                      </Button>
                    </div>
                  </template>
                </DetailBanner>
              </div>
            </div>
          </div>
        </div>
      </template>
    </Modal>
    <!---------- Investor Dashboard Hero ----------->
    <HeroSection />
    <!---------- Action List ----------->
    <div class="md:flex items center md:space-x-4 space-y-3 md:space-y-0 mt-5">
      <DetailBanner
        v-for="actionItem in visibleActionItems"
        :key="actionItem.id"
        :variant="actionItem.variant"
        :has_error_outline="actionItem.has_error_outline"
        :has_info_outline="actionItem.has_info_outline"
        :has_warning_outline="actionItem.has_warning_outline"
        :has_success_outline="actionItem.has_success_outline"
        :class="actionItem.class"
        :button_flex="
          (visibleActionItems.length == 1 && actionItem.priority == 1) || actionItem.button_flex
        "
      >
        <template #body>
          <AsteriskNotice
            :variant="actionItem.asterisk_variant"
            size="medium"
            :text="actionItem.asterisk_text"
            class="mb-1"
          />
          <div
            class="text-2xl font-medium font-stretch-normal not-italic leading-none tracking-[-0.56] text-left text-strongest"
          >
            {{ actionItem.header_text }}
          </div>
          <span
            v-for="(segment, i) in actionItem.body_text"
            :key="i"
            class="font-inter text-sm font-medium font-stretch-normal not-italic leading-none tracking-normal text-left"
            :class="segment.class"
          >
            {{ segment.text }}
          </span>
        </template>
        <template #ctas>
          <div>
            <Button
              :variant="actionItem.button_variant"
              @click="
                () => {
                  dashboardCtaClicked(actionItem.button_label);
                  actionItem.action();
                }
              "
            >
              <template #label>{{ actionItem.button_label }}</template>
            </Button>
          </div>
        </template>

        <template v-if="actionItem.dismissible" #close-button>
          <div class="absolute top-2 right-2">
            <button @click="dismissActionItem(actionItem)">
              <Icon name="X" class="w-4 h-4 text-strong" />
            </button>
          </div>
        </template>
      </DetailBanner>
      <button
        @click="showMoreActionsModal = true"
        v-if="overflowActionItems.length"
        class="bg-container-bg rounded-2xl p-5 flex items-start"
      >
        <div class="space-y-3">
          <div class="relative w-5">
            <div
              class="absolute -top-1 -right-2.5 bg-red-600 rounded-xl px-2 font-bold tracking-0 text-xs text-white"
            >
              {{ overflowActionItems.length }}
            </div>
            <Icon name="Bell" class="w-5 h-5 text-strongest" />
          </div>

          <div
            class="text-2xl font-medium font-stretch-normal not-italic leading-none tracking-[-0.48] text-left text-strongest"
          >
            View {{ overflowActionItems.length }} More
          </div>
        </div>
      </button>
    </div>
    <!---------- Earnings Table ----------->
    <div v-if="dashboard_table_data.length">
      <h1
        class="text-[2rem] font-semibold font-stretch-normal not-italic leading-none tracking-[-0.64] text-left text-strongest mb-5 mt-16"
      >
        Your returns
      </h1>
      <Table
        :table_headers="dashboard_table_headers"
        :table_data="dashboard_table_data.slice(0, 3)"
        :hide_headers="false"
        :hide_scrollbar="true"
        mobile_primary_field="amount"
        variant="secondary"
        :has_actions="false"
        :fields_with_custom_styles="['amount']"
        :column_gap_px="85"
        default_sort_field="distribution_date"
        :default_sort_ascending="false"
      >
        <template v-slot:amount="entry">
          <div
            class="font-inter text-base font-semibold font-stretch-normal not-italic leading-none tracking-normal text-left text-success"
          >
            {{ formatCurrency(entry.row.amount) }}
          </div>
        </template>
      </Table>
      <button
        @click="router.push('/portfolio')"
        class="w-full px-8 py-4 mb-16 flex items-center justify-center rounded-full bg-container-bg text-strongest font-inter font-semibold mt-5 md:mt-0"
      >
        <Icon name="List" class="w-6 h-6 mr-2" /> View All
      </button>
    </div>
    <!---------- Investment Options ----------->
    <!-- <OfferingsScreen class="my-16" :parentScreen="'Dashboard'">
      <template v-slot:header>
        <div class="md:flex justify-between items-center">
          <div
            class="font-semibold text-3xl font-stretch-normal not-italic leading-none tracking-[-0.64] text-left text-strongest mb-4"
          >
            Explore our bond offerings
          </div>
        </div>
      </template>
    </OfferingsScreen> -->
    <OfferingsScreenV3 class="my-16" :parentScreen="'Dashboard'"> </OfferingsScreenV3>
    <Dashboard_LatestUpdates>
      <div
        class="mb-5 text-[2rem] font-semibold font-stretch-normal not-italic leading-none tracking-[-0.64] text-left text-strongest"
      >
        Latest Updates
      </div>
    </Dashboard_LatestUpdates>
    <!-- <Newsfeed_PhoenixFundamentals>
      <div
        class="mb-5 text-[2rem] font-semibold font-stretch-normal not-italic leading-none tracking-[-0.64] text-left text-strongest"
      >
        Phoenix Fundamentals
      </div>
    </Newsfeed_PhoenixFundamentals> -->
  </div>
</template>

<script setup>
// script for the investor dashboard
import { ref, onMounted, watch, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import {
  Button,
  Icon,
  Table,
  DetailBanner,
  AsteriskNotice,
  S1DisclaimerModal,
  SignPendingS1InvestmentsModal,
  OptOutS1InvestmentsModal,
  SignInvestmentLoader,
  Modal,
  HeroSection,
  DocumentPreviewModal,
} from "@/components";
import {
  useUIStore,
  useDataStore,
  useInvestmentStore,
  useAuthStore,
  usePurchaseFlowStore,
  useCrudStore,
  useSchemaStore,
} from "@/stores";
import OfferingsScreen from "../PurchaseFlow/OfferingsScreen.vue";
import {
  isExpirationWithinNext3Months,
  bankIsExpired,
  isRetirementAccountType,
} from "@/utilities/index.js";
import useFileUtility from "@/composables/useFileUtility";
import { useAnalytics } from "@/composables/useAnalytics";
import Dashboard_LatestUpdates from "./Dashboard_LatestUpdates.vue";
import Newsfeed_PhoenixFundamentals from "../Newsfeed/Newsfeed_PhoenixFundamentals.vue";
import ReviewEditModal from "../PurchaseFlow/PurchaseFlowUtilities/ReviewEditModal.vue";
import preparingDocLight from "@/assets/animations/preparing-document-light.json";
import preparingDocDark from "@/assets/animations/preparing-document-dark.json";
import OfferingsScreenV3 from "../PurchaseFlow/OfferingsScreenV3.vue";

const router = useRouter();
const route = useRoute();
const UIStore = useUIStore();
const dataStore = useDataStore();
const investmentStore = useInvestmentStore();
const authStore = useAuthStore();
const purchaseFlowStore = usePurchaseFlowStore();
const crudStore = useCrudStore();
const schemaStore = useSchemaStore();

const { fetchFileForPreview_R2 } = useFileUtility();
const { dashboardViewed, dashboardCtaClicked, makeInvestmentClicked } = useAnalytics();

const is_s1_disclaimer_modal_open = ref(false);
const showMoreActionsModal = ref(false);
const actionItems = ref([]);
const selectedComponentDetails = ref(null);
const isReviewEditModalOpen = ref(false);
const is_sign_pending_s1_modal_open = ref(false);
const is_opt_out_s1_modal_open = ref(false);
const download_is_loading = ref(false);
const is_statement_preview_modal_open = ref(false);
const statement_preview_file = ref(null);

const visibleActionItems = computed(() => {
  const visible = [];
  let remaining = 4;

  for (const item of actionItems.value.sort((a, b) => a.priority - b.priority)) {
    if (item.size <= remaining) {
      visible.push(item);
      remaining -= item.size;
    }
  }

  return visible;
});

const overflowActionItems = computed(() =>
  actionItems.value.slice(visibleActionItems.value.length)
);

const groupedActionItems = computed(() => {
  return actionItems.value.reduce((acc, item) => {
    const priority = item.priority || "default";

    if (!acc[priority]) {
      acc[priority] = [];
    }

    acc[priority].push(item);
    return acc;
  }, {});
});

onMounted(async () => {
  UIStore.is_chart_loading = true;
  window.scrollTo(0, 0);
  await getDashboardData();
  await findPNCAcctsExpiring();
  dashboardViewed();
  if (dataStore.s1_offerings_in_quiet_period) {
    setTimeout(() => {
      if (authStore.is_first_login) {
        is_s1_disclaimer_modal_open.value = true;
      }
      authStore.is_first_login = false;
    }, 500);
  }
});

let dashboard_table_headers = ref([
  { label: "Date", field_name: "distribution_date", field_type: "date" },
  { label: "Total Amount", field_name: "amount", field_type: "number", number_type: "currency" },
  {
    label: "Monthly payments",
    field_name: "simple_amount",
    field_type: "number",
    number_type: "currency",
  },
  {
    label: "Accrued interest",
    field_name: "compounding_amount",
    field_type: "number",
    number_type: "currency",
  },
]);

let dashboard_table_data = ref([]);
// ── Action-item source data (each computed drives one or more banners) ──
const optOutInvestments = computed(() => {
  if (!dataStore.investments?.length) return null;

  return dataStore.investments?.filter((inv) => {
    if (!inv?.is_short_term || !inv?.maturity_date || inv?.opt_out_decision_complete) return false;
    const maturityDate = new Date(inv.maturity_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const daysUntilMaturity = Math.ceil((maturityDate - today) / (1000 * 60 * 60 * 24));
    return daysUntilMaturity >= 30 && daysUntilMaturity <= 45;
  });
});

const unfinishedInvestment = computed(() => {
  if (!dataStore.investments?.length) return null;

  return dataStore.investments?.filter(
    (investment) =>
      !investment.signature_date &&
      investment.status !== "Pending" &&
      investment.status !== "Pending S1"
  );
});

const rolloversAvailable = computed(() => {
  if (!dataStore.investments?.length) return [];

  return dataStore.investments?.filter(
    (investment) =>
      investment.able_to_rollover &&
      investment.status === "Settled" &&
      investment.rollover_available >= 1000
  );
});

const tempInvestment = computed(() =>
  crudStore.findOne("tempinvestments", [
    { field_name: "contact_id", operator: "eq", value: dataStore.contact?.record_id },
  ])
);

// ─────────────────────────────────────────────────────────────────────────
// Action-item registry
//
// Every dashboard banner is declared here as a single entry. To change what
// shows, and in what order, edit THIS list — nothing else.
//
//   key       – stable identifier. Used for dismissal, so it survives copy
//               edits (unlike matching on header_text).
//   rank      – global ordering; LOWER shows first. Gaps of 10 leave room to
//               slot a new item between two existing ones without renumbering.
//   condition – returns true when the banner should appear.
//   build     – returns the banner's display config. `priority` (1 = Urgently
//               required, 2 = Required, 3 = Great offer) is used only for the
//               "View More" modal's section grouping; `size` feeds the
//               4-unit visible-row budget in `visibleActionItems`.
// ─────────────────────────────────────────────────────────────────────────
const actionItemDefinitions = [
  {
    key: "phoenix_flex_notice",
    rank: 10,
    condition: () => optOutInvestments.value?.length > 0,
    build: () => ({
      variant: "secondary",
      has_error_outline: false,
      has_info_outline: true,
      header_text: "Phoenix Flex Notice Window Open",
      body_text: [
        {
          text: "Keep earning with Phoenix Flex or make a redemption request before your window closes.",
          class: "text-strong",
        },
      ],
      asterisk_variant: "info",
      asterisk_text: "Required action",
      body_color: "text-strong",
      button_variant: "primary-grey",
      button_label: "View Details",
      button_flex: false,
      size: 3,
      class: "w-full",
      action: () => {
        is_opt_out_s1_modal_open.value = true;
      },
      priority: 1,
    }),
  },
  {
    key: "investment_created",
    rank: 20,
    condition: () => createdInvestments.value?.length > 0,
    build: () => {
      const count = createdInvestments.value.length;
      const isSingle = count === 1;
      return {
        variant: "secondary",
        has_error_outline: false,
        has_info_outline: true,
        has_warning_outline: false,
        has_success_outline: false,
        header_text: "Investment Created",
        body_text: [
          {
            text: isSingle
              ? "Your investment has been submitted and is being processed. We'll confirm once it's finalized."
              : `You have ${count} investments being submitted and processed. We'll send you an email once they're settled.`,
            class: "text-strong",
          },
        ],
        asterisk_variant: "info",
        asterisk_text: "Investment submitted",
        body_color: "text-strong",
        button_variant: "primary-grey",
        button_label: isSingle ? "View Investment" : "View Investments",
        button_flex: false,
        size: 3,
        class: "w-full",
        action: () => {
          if (isSingle) {
            router.push("/investment-details?recordId=" + createdInvestments.value[0].record_id);
          } else {
            router.push("/portfolio?selectedTab=investments");
          }
        },
        priority: 1,
      };
    },
  },
  {
    key: "pending_signatures",
    rank: 30,
    condition: () => pendingS1Investments.value?.length > 0,
    build: () => ({
      variant: "secondary",
      has_error_outline: true,
      header_text: "Pending Signatures",
      body_text: [
        {
          text: "You have pending Registered Offering (S1) investments that require your signature.",
          class: "text-strong",
        },
      ],
      asterisk_variant: "error",
      asterisk_text: "Required action",
      body_color: "text-strong",
      button_variant: "primary-grey",
      button_label: "View Investments",
      button_flex: false,
      size: 3,
      class: "w-full",
      action: () => {
        is_sign_pending_s1_modal_open.value = true;
      },
      priority: 1,
    }),
  },
  {
    key: "finish_pending_investment",
    rank: 40,
    condition: () =>
      unfinishedInvestment.value?.[0] &&
      unfinishedInvestment.value?.[0].last_completed_step &&
      !unfinishedInvestment.value?.[0].dismiss_pending_investment_action,
    build: () => ({
      variant: "secondary",
      has_error_outline: true,
      header_text: "Finish your pending investment",
      body_text: [
        {
          text:
            "Just a few steps away from " +
            (unfinishedInvestment.value[0].interest_rate
              ? unfinishedInvestment.value[0].interest_rate
              : purchaseFlowStore.selected_offering.interest_rate) +
            "% returns.",
          class: "text-strong",
        },
      ],
      asterisk_variant: "error",
      asterisk_text: "Action needed",
      body_color: "text-strong",
      button_variant: "primary-grey",
      button_label: "Finish investment",
      button_flex: false,
      action: () => {
        makeInvestmentClicked("dashboard_resume_pending");
        router.push("/make-an-investment?finishPendingInvestment=true");
      },
      size: 3,
      class: "w-full",
      priority: 1,
      dismissible: true,
    }),
  },
  {
    key: "id_expired",
    rank: 50,
    condition: () => {
      const expiration = dataStore.contact?.id_info?.id_expiration_date;
      if (!expiration) return false;
      return new Date(expiration).getTime() < new Date().getTime();
    },
    build: () => ({
      variant: "secondary",
      has_error_outline: true,
      header_text: "",
      body_text: [
        {
          text: "Your ID on file has expired. Please update your ID info to continue investing.",
          class: "text-strong",
        },
      ],
      asterisk_variant: "error",
      asterisk_text: "Required Action",
      body_color: "text-strong",
      button_variant: "paper-primary",
      button_label: "Update ID",
      button_flex: false,
      size: 1,
      class: "md:w-[400px]",
      action: () => {
        router.push("/profile?editSection=id-information");
      },
      priority: 1,
    }),
  },
  {
    key: "pnc_bank_refresh",
    rank: 60,
    condition: () => purchaseFlowStore.PNCAcctsExpiring?.length > 0,
    build: () => ({
      variant: "secondary",
      has_error_outline: true,
      header_text: "Bank connection refresh needed",
      body_text: [
        {
          text: "As part of our regular security checks, we need you to reconnect your PNC bank accounts.",
          class: "text-strong",
        },
      ],
      asterisk_variant: "error",
      asterisk_text: "Required action",
      body_color: "text-strong",
      button_variant: "primary-grey",
      button_label: "Reconnect bank accounts",
      button_flex: false,
      action: () => {
        router.push("/profile?selectedTab=banking");
      },
      size: 3,
      class: "w-full",
      priority: 1,
    }),
  },
  {
    key: "reinvestment_available",
    rank: 70,
    condition: () => rolloversAvailable.value.length > 0,
    build: () => ({
      variant: "secondary",
      has_error_outline: false,
      header_text: "",
      body_text: [
        {
          text: "Reinvestment available, up to 13% annual return",
          class: "text-strong",
        },
      ],
      asterisk_variant: "info",
      asterisk_text: "Investment opportunity",
      body_color: "text-strong",
      button_variant: "paper-primary",
      button_label: "View Investments",
      button_flex: false,
      size: 1,
      class: "md:w-[400px]",
      action: () => {
        router.push("/portfolio?selectedTab=reinvest");
      },
      priority: 2,
    }),
  },
  {
    key: "conversion_pending",
    rank: 80,
    condition: () => tempInvestment.value?.conversion_parent_ids?.length > 0,
    build: () => ({
      variant: "secondary",
      has_error_outline: false,
      header_text: "",
      body_text: [
        {
          text: "You have an investment pending conversion",
          class: "text-strong",
        },
      ],
      asterisk_variant: "info",
      asterisk_text: "Investment pending",
      body_color: "text-strong",
      button_variant: "paper-primary",
      button_label: "View Investment",
      button_flex: false,
      size: 1,
      class: "md:w-[400px]",
      action: () => {
        router.push(
          "/investment-details?recordId=" + tempInvestment.value.conversion_parent_ids[0]
        );
      },
      priority: 2,
    }),
  },
  {
    key: "new_statement",
    rank: 90,
    condition: () => new Date().getDate() === 10,
    build: () => ({
      variant: "secondary",
      has_error_outline: false,
      header_text: "New Statement Available",
      body_text: [
        {
          text: "You have a new statement available to view.",
          class: "text-strong",
        },
      ],
      asterisk_variant: "info",
      asterisk_text: "New Documents",
      body_color: "text-strong",
      button_variant: "paper-primary",
      button_label: "View statement",
      button_flex: false,
      size: 1,
      class: "md:w-[400px]",
      action: () => {
        previewStatement();
      },
      priority: 2,
    }),
  },
];

// Single source of truth for the on-screen list: evaluate every definition's
// condition, order by rank, then assign sequential ids. Reassigns (does not
// push), so it is idempotent and safe to call on every data refresh.
function buildActionItems() {
  actionItems.value = actionItemDefinitions
    .filter((definition) => definition.condition())
    .sort((a, b) => a.rank - b.rank)
    .map((definition, index) => ({
      id: index + 1,
      key: definition.key,
      ...definition.build(),
    }));
}

watch(
  () => dataStore.is_app_initialized,
  async function () {
    await getDashboardData();
  }
);

async function getDashboardData() {
  if (dataStore.is_app_initialized) {
    UIStore.sort_ascending = true;
    dashboard_table_data.value = await investmentStore.getInvestorEarnings();
    UIStore.is_chart_loading = false;

    buildActionItems();

    UIStore.is_chart_loading = false;
  }
}
async function findPNCAcctsExpiring() {
  const now = new Date();
  const threeMonthsFromNow = new Date();
  threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);

  let PNC_Banks = dataStore.bank_accounts.filter((acct) => acct.institution_id === "ins_13");

  purchaseFlowStore.PNCAcctsExpiring = [];

  for (let acct of PNC_Banks) {
    if (
      isExpirationWithinNext3Months(
        acct.created_date,
        acct.financial_institution_name,
        acct.plaid_account_id
      ) ||
      bankIsExpired(acct.created_date, acct.financial_institution_name, acct.plaid_account_id)
    ) {
      purchaseFlowStore.PNCAcctsExpiring.push(acct);
    }
  }
  buildActionItems();
}
function formatCurrency(value) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value);
}

// Opens the statement in the shared preview modal. The modal's own footer still
// offers a download, so this is view-first rather than download-on-click.
async function previewStatement() {
  if (!dataStore?.statement) {
    await UIStore.animateNotificationAlert({
      type: "error",
      message: "No statement available to preview.",
      subText: "",
      is_read: false,
      does_persist: false,
      action: null,
      icon: "X",
    });
    return;
  }
  download_is_loading.value = true;
  try {
    const file_url = await fetchFileForPreview_R2(dataStore.statement);

    statement_preview_file.value = {
      file_name: dataStore.statement.file_name,
      file_type: dataStore.statement.file_type,
      file_url: file_url,
      entry: dataStore.statement,
    };
    // Close the notifications modal so the preview is not stacked behind it
    showMoreActionsModal.value = false;
    is_statement_preview_modal_open.value = true;
  } catch {
    // fetchFileForPreview_R2 surfaces its own error alert
  }
  download_is_loading.value = false;
}

function closeStatementPreview() {
  is_statement_preview_modal_open.value = false;
}

// S1 Utilities
async function handleGeneratePendingS1SigningLink(investment_id) {
  if (purchaseFlowStore.signing_link_invalidated || !purchaseFlowStore.signing_link) {
    UIStore.is_loading = true;
    await purchaseFlowStore.generateSigningLink(investment_id);
  }

  // Wait out the remaining 15s buffer
  const remaining = purchaseFlowStore.signingLinkTimeRemaining;
  if (remaining > 0) {
    UIStore.is_loading = true;
    await new Promise((resolve) => setTimeout(resolve, remaining));
  }

  window.location.href = purchaseFlowStore.signing_link;
  UIStore.is_loading = false;
}

async function dismissActionItem(item) {
  if (item.key === "finish_pending_investment") {
    let update = {
      dismiss_pending_investment_action: true,
    };

    update = schemaStore.addCreatedUpdatedFields(update, false, true);

    await crudStore.update(
      "investments",
      [
        {
          field_name: "record_id",
          operator: "eq",
          value: unfinishedInvestment.value?.[0].record_id,
        },
      ],
      update
    );
    if (unfinishedInvestment.value?.[0]) {
      unfinishedInvestment.value[0].dismiss_pending_investment_action = true;
    }
    actionItems.value = actionItems.value.filter((action) => action.key !== item.key);
  }
}

const pendingS1Investments = computed(() => {
  return (
    dataStore.investments?.filter(
      (investment) =>
        investment.status === "Pending S1" &&
        !dataStore.s1_offerings_in_quiet_period &&
        !investment.signature_date &&
        !isRetirementAccountType(investment.type)
    ) ?? []
  );
});

// Investments that have been submitted and are being processed (freshly completed).
// A submitted investment is signed (`signature_date` set) but stays in a pre-processing
// status until back-office processing moves it forward — this is "Created" for standard/IRA
// investments (the status label is misleading; it does not mean an in-progress draft) and
// "Pending S1" for the S1 quiet-period path. Requiring a signature_date excludes the two
// look-alikes handled by other banners: abandoned "Created" drafts ("Finish your pending
// investment") and unsigned S1s awaiting signature ("Pending Signatures").
const createdInvestments = computed(() => {
  return (
    dataStore.investments?.filter(
      (investment) =>
        // If has signature date & created OR is pending
        (investment.signature_date && investment.status === "Created") ||
        investment.status === "Pending"
    ) ?? []
  );
});
const animationData = computed(() => (UIStore.is_dark_mode ? preparingDocDark : preparingDocLight));
</script>
